import React, { useMemo, useEffect, useState } from 'react';
import { Stack, Autocomplete, TextField, Checkbox, Skeleton, Alert } from '@mui/material';
import { Grid, pn, useElementBreakpoints } from '@compass/core';
import { Select } from 'components';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {
    StudentFilter as StudentFilterModel,
    StudentFilterResource,
    LkStudentDisplayFilterId,
} from 'generated/openApi';
import { useEndReasonTypeData } from '../contexts/EndReasonTypeDataContext';
import ApiService from 'services/apiService';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export const EndReasonTypeFilterControls = () => {
    const { breakpointsRef, smallFieldBreakpoints } = useElementBreakpoints();
    const { 
        filter, 
        setFilter, 
        activeSchoolYear, 
        endReasonOptions, 
        optionsLoading,
        optionsError,
    } = useEndReasonTypeData();

    // Fetch StudentFilterResource for program filter
    const [resource, setResource] = useState<StudentFilterResource>({} as StudentFilterResource);
    const properties = resource.properties || [];

    useEffect(() => {
        ApiService.students.getFilterResource().then((response) => {
            setResource(response);
        });
    }, []);

    const availableYears = useMemo(() => {
        if (!activeSchoolYear) return [];
        return Array.from({ length: 10 }, (_, i) => {
            const val = activeSchoolYear - i;
            return { value: val, label: `${val}-${val + 1}` };
        });
    }, [activeSchoolYear]);

    const programField = () => {
        const propertyName = pn<StudentFilterModel>("programIds");
        const property = properties.find((x) => x.propertyName === propertyName);

        return (
            <Select
                modelProperty={property}
                value={resource.programs?.find((x) => x.id === filter.programId)}
                options={resource.programs}
                getOptionLabel={(option) => option.name}
                onChange={(e, value) => setFilter({ programId: value?.id ?? null })}
            />
        );
    };

    // Check if tenant should see the program filter
    const showProgramFilter = resource.displayFilters?.some(
        x => x.id === LkStudentDisplayFilterId.Program
    );

    if (optionsLoading) {
        return (
            <Stack spacing={2} sx={{ mb: 2 }}>
                <Skeleton variant="rounded" height={40} />
                <Skeleton variant="rounded" height={40} />
            </Stack>
        );
    }

    if (optionsError) {
        return <Alert severity="error" sx={{ mb: 2 }}>{optionsError}</Alert>;
    }

    if (!activeSchoolYear && availableYears.length === 0) return null;

    const selectedYearOptions = availableYears.filter(y => filter.schoolYears.includes(y.value));
    const selectedTypeOptions = endReasonOptions.filter(t => filter.selectedEndReasonTypes?.includes(t.id));

    return (
        <Stack spacing={2} sx={{ mb: 2 }}>
            {/* Row 1: School Year and Program filters */}
            <Grid ref={breakpointsRef} container spacing="sm">
                <Grid item xs={smallFieldBreakpoints} sx={{ minWidth: 200 }}>
                    <Autocomplete
                        multiple
                        options={availableYears}
                        value={selectedYearOptions}
                        onChange={(_, newValue) => setFilter({ schoolYears: newValue.map(v => v.value) })}
                        disableCloseOnSelect
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        limitTags={2}
                        renderOption={(props, option, { selected }) => (
                            <li {...props}>
                                <Checkbox icon={icon} checkedIcon={checkedIcon} sx={{ marginRight: 8 }} checked={selected} />
                                {option.label}
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="School Year" placeholder="Select years" />}
                    />
                </Grid>

                {showProgramFilter && (
                    <Grid item xs={smallFieldBreakpoints}>
                        {programField()}
                    </Grid>
                )}
            </Grid>

            {/* Row 2: End Reason Type filter */}
            <Grid container spacing="sm">
            <Grid item xs={12}>
                <Autocomplete
                    multiple
                    size="small"
                    options={endReasonOptions}
                    value={selectedTypeOptions}
                    onChange={(_, newValue) => setFilter({ selectedEndReasonTypes: newValue.map(v => v.id) })}
                    disableCloseOnSelect
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    limitTags={2}
                    renderOption={(props, option, { selected }) => (
                        <li {...props}>
                            <Checkbox icon={icon} checkedIcon={checkedIcon} sx={{ marginRight: 8 }} checked={selected} />
                            {option.name}
                        </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="End Reason Type" placeholder="All Types" />}
                />
            </Grid>
        </Grid>
        </Stack>
    );
};